import type { CardTemplate } from '../templates/types';
import { TEMPLATE_CATEGORIES } from '../constants';

/** How many of the product's pages this template actually designs. */
export function templateCoverage(
  tpl: Pick<CardTemplate, 'slots'>,
  productSlotKeys: string[],
): { designed: number; total: number } {
  const keys = new Set(productSlotKeys);
  const designed = tpl.slots.filter((s) => keys.has(s.slotKey)).length;
  return { designed, total: productSlotKeys.length };
}

/** Slugs present in the given templates, in the canonical TEMPLATE_CATEGORIES
 *  order. Unknown slugs (junk data) are dropped rather than rendered. */
export function availableCategories(templates: Array<Pick<CardTemplate, 'categories'>>): string[] {
  const present = new Set(templates.flatMap((t) => t.categories ?? []));
  return TEMPLATE_CATEGORIES.filter((c) => present.has(c));
}

export function filterByCategory<T extends Pick<CardTemplate, 'categories'>>(
  templates: T[],
  category: string | null,
): T[] {
  if (!category) return templates;
  return templates.filter((t) => (t.categories ?? []).includes(category));
}

// ─────────────────────────────────────────────────────────────────────────────
// Which templates a product's picker offers, and in what order.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Menus that live under the invitation kind. The six `menu-*` designs
 * (category `cafe`) were authored as invitation-kind templates because they
 * share the invitation page, but they are menus: a customer looking for a
 * wedding invitation got a drinks list among the first results (they also
 * carry `wedding` and `birthday`). Both signals are checked — the code prefix
 * because it is how they were named, the category because it is what the data
 * model says — so either one being fixed later keeps them out.
 */
export function isMenuTemplate(tpl: { id?: string; code?: string; categories?: string[] }): boolean {
  const code = (tpl.code ?? tpl.id ?? '').toLowerCase();
  return code.startsWith('menu-') || (tpl.categories ?? []).includes('cafe');
}

/** Templates the picker for a product of `kind` should show. */
export function pickerTemplatesForKind<T extends { id: string; categories: string[] }>(
  kind: string | undefined,
  templates: T[],
): T[] {
  if (kind === 'invitation') return templates.filter((t) => !isMenuTemplate(t));
  return templates;
}

/**
 * Occasion order per product kind, most-wanted first. Style slugs (elegant,
 * modern, fun) are not occasions and follow in canonical order. Kinds without
 * an entry keep the canonical TEMPLATE_CATEGORIES order.
 *
 * Invitations: weddings are by far the largest invitation market (and what the
 * marketing site leads with), then birthdays, christenings/communions, baby
 * showers. The backend's sortOrder put all 13 birthday designs first, so an
 * unfiltered picker opened on balloons and cars.
 */
export const KIND_CATEGORY_ORDER: Record<string, string[]> = {
  invitation: ['wedding', 'birthday', 'faith', 'baby', 'holiday', 'family', 'love', 'thankyou', 'congrats'],
};

const STYLE_CATEGORIES = new Set(['elegant', 'modern', 'fun']);

function categoryOrderFor(kind: string | undefined): string[] {
  const preferred = (kind && KIND_CATEGORY_ORDER[kind]) || [];
  const rest = TEMPLATE_CATEGORIES.filter((c) => !preferred.includes(c));
  return [...preferred, ...rest.filter((c) => !STYLE_CATEGORIES.has(c)), ...rest.filter((c) => STYLE_CATEGORIES.has(c))];
}

/** Chip order for a kind: `availableCategories`, re-ordered for the kind. */
export function orderedCategories(
  templates: Array<Pick<CardTemplate, 'categories'>>,
  kind: string | undefined,
): string[] {
  if (!kind || !KIND_CATEGORY_ORDER[kind]) return availableCategories(templates);
  const present = new Set(availableCategories(templates));
  return categoryOrderFor(kind).filter((c) => present.has(c));
}

/**
 * Sort templates for the unfiltered "all" view: by the best-ranked occasion
 * each template belongs to, keeping the backend's order within an occasion
 * (stable sort). Kinds without a preferred order are returned unchanged.
 */
export function sortTemplatesForKind<T extends Pick<CardTemplate, 'categories'>>(
  templates: T[],
  kind: string | undefined,
): T[] {
  if (!kind || !KIND_CATEGORY_ORDER[kind]) return templates;
  const order = categoryOrderFor(kind);
  const rank = (t: T) => {
    const ranks = (t.categories ?? [])
      .filter((c) => !STYLE_CATEGORIES.has(c))
      .map((c) => order.indexOf(c))
      .filter((i) => i >= 0);
    return ranks.length ? Math.min(...ranks) : order.length;
  };
  return templates
    .map((t, i) => ({ t, i, r: rank(t) }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map(({ t }) => t);
}

/**
 * The template a `?template=<code>` deep link names, or null. Codes are
 * compared case-insensitively and trimmed; anything that does not match a
 * template the picker offers (unknown, inactive, or a menu on an invitation)
 * is ignored.
 */
export function findTemplateByCode<T extends { id: string }>(
  templates: T[],
  code: string | null | undefined,
): T | null {
  const wanted = (code ?? '').trim().toLowerCase();
  if (!wanted) return null;
  return templates.find((t) => t.id.toLowerCase() === wanted) ?? null;
}
